"use client"

import {
  PortableText as PortableTextComponent,
  type PortableTextComponents,
  type PortableTextBlock,
} from "@portabletext/react"

import {
  ImageBlock,
  CodeBlockPremium,
  CalloutBlock,
  DiagramBlock,
  ComparisonTableBlock,
} from "@/core/components/sanity-custom-types"

type portableTextBlock = PortableTextBlock & { children?: any[] }

const components: PortableTextComponents = {
  types: {
    image: ({ value }) => <ImageBlock value={value} />,
    codeBlock: ({ value }) => <CodeBlockPremium value={value} />,
    callout: ({ value }) => <CalloutBlock value={value} />,
    diagram: ({ value }) => <DiagramBlock value={value} />,
    comparisonTable: ({ value }) => <ComparisonTableBlock value={value} />,
  },
  block: {
    h2: ({ children, value }: any) => {
      const text = value?.children?.map((c: any) => c.text).join('') || ""
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      return (
        <h2 id={id} className="text-3xl font-bold text-white mt-20 mb-8 tracking-tight scroll-mt-32">
          {children}
        </h2>
      )
    },
    h3: ({ children, value }: any) => {
      const text = value?.children?.map((c: any) => c.text).join('') || ""
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      return (
        <h3 id={id} className="text-2xl font-semibold text-white/90 mt-14 mb-6 tracking-tight scroll-mt-32">
          {children}
        </h3>
      )
    },
    h4: ({ children }) => (
      <h4 className="text-xl font-semibold text-white/80 mt-10 mb-4">
        {children}
      </h4>
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-10 relative overflow-hidden rounded-2xl border-l-[3px] border-premium-cyan bg-black/50 backdrop-blur-md p-6 lg:p-8 shadow-[inset_0px_0px_20px_rgba(255,255,255,0.02)]">
        <div className="absolute top-0 right-0 w-32 h-32 bg-premium-cyan/10 blur-3xl -mr-16 -mt-16 rounded-full opacity-50" />
        <div className="relative z-10 italic text-slate-300 text-lg md:text-xl font-light leading-relaxed">
          {children}
        </div>
      </blockquote>
    ),
    normal: ({ children }) => (
      <p className="text-[#B0B0B0] text-lg leading-[1.85] font-light mb-8 antialiased">
        {children}
      </p>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-inside space-y-2 my-6 text-[#A0A0A0] font-light">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-inside space-y-2 my-6 text-[#A0A0A0] font-light">
        {children}
      </ol>
    ),
  },
  marks: {
    strong: ({ children }) => (
      <strong className="text-white font-medium">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ children }) => (
      <code className="bg-white/10 text-premium-cyan px-1.5 py-0.5 rounded text-sm font-mono">
        {children}
      </code>
    ),
    link: ({
      children,
      value,
    }: {
      children: React.ReactNode
      value?: { href?: string; blank?: boolean }
    }) => {
      const target = value?.blank ? "_blank" : undefined
      const rel = value?.blank ? "noopener noreferrer" : undefined
      return (
        <a
          href={value?.href}
          target={target}
          rel={rel}
          className="text-white underline decoration-white/30 underline-offset-4 hover:decoration-premium-violet hover:text-premium-violet transition-all"
        >
          {children}
        </a>
      )
    },
    internalLink: ({
      children,
      value,
    }: {
      children: React.ReactNode
      value?: { href?: string }
    }) => {
      return (
        <a
          href={value?.href || "#"}
          className="text-white underline decoration-white/30 underline-offset-4 hover:decoration-premium-cyan hover:text-premium-cyan transition-all"
        >
          {children}
        </a>
      )
    },
  },
}

interface PortableTextProps {
  value: PortableTextBlock[]
}

export function PortableText({ value }: PortableTextProps) {
  return <PortableTextComponent value={value} components={components} />
}
